import { makeStyles, Theme } from '@material-ui/core';

const CALENDAR_MOBILE_BREAKPOINT = '642px';

type MakeStylesProp = {
  widgetMode: boolean;
};

/**
 * Brute-forced styling to make the backstage table component responsive
 * This is an attempt at making the event calendar easier to view and use on
 * smaller screens
 */
const useStyles = makeStyles<Theme, MakeStylesProp>(theme => ({
  containerWrapper: {
    containerType: 'inline-size',
    width: '100%',
  },
  tableContainer: ({ widgetMode }) => ({
    [`@container (max-width: ${CALENDAR_MOBILE_BREAKPOINT})`]: {
      width: '100%',
      '& table': {
        width: '100%',
        '& tbody': {
          display: 'block',
        },
        '& thead': {
          display: 'none',
        },
      },

      '& tr': {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateAreas: `
          "title title"
          "datetime datetime"
          "location location"
          "action tags"
        `,
        width: '100%',
        boxSizing: 'border-box',
        padding: '0.5em',
        gap: '0.5em',
        borderBottom: 'solid 1px light-dark(rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0.2))',
        borderLeft: 'solid 1px light-dark(rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0.2))',
        borderRight: 'solid 1px light-dark(rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0.2))',
      },

      '& tbody tr:first-of-type': {
        borderTop: 'solid 1px light-dark(rgba(0, 0, 0, 0.2), rgba(255, 255, 255, 0.2))',
      },

      '& td': {
        width: 'auto !important',
        padding: '0.5em !important',
      },

      '& td:has([data-calendar-cell="datetime"])': {
        gridArea: 'datetime',
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5em',
        '&::before': {
          content: '"When: "',
          fontWeight: theme.typography.fontWeightBold,
          whiteSpace: 'nowrap',
        },
        '& div[data-calendar-cell="datetime"]': {
          display: 'flex',
          flexDirection: 'row',
          gap: '0.5em',
          flexWrap: 'wrap',
        },
      },

      '& td:has([data-calendar-cell="title"])': {
        gridArea: 'title',
      },

      '& td:has([data-calendar-cell="tags"])': {
        textAlign: 'left',
        gridArea: 'tags',
      },

      '& td:has([data-calendar-cell="location"])': {
        textAlign: 'left',
        gridArea: 'location',
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5em',
        '&::before': {
          content: '"Where: "',
          fontWeight: theme.typography.fontWeightBold,
        },
      },

      '& td:has([data-calendar-cell="action"])': {
        textAlign: 'left',
        gridArea: 'action',
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5em',
        alignItems: 'center',

        '& .bui-Flex': {
          justifyContent: 'start',
        },

        '&:not(:has(button))::before': {
          content: '"Status: "',
          fontWeight: theme.typography.fontWeightBold,
          whiteSpace: 'nowrap',
        },
      },

      '& [data-calendar-cell-value="title"]': {
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: '1.5em',
      },

      '& [class*="BackstageTableToolbar-root"]': {
        flexDirection: 'column-reverse',

        '& [class*="BackstageTableToolbar-searchField"]': {
          paddingBottom: 'var(--bui-space-4)',
        },
      },
    },
    '& [class*="BackstageTableToolbar-title"] h2': {
      margin: '0',
    },
    '& [class*="BackstageTableToolbar-root"], div[data-calendar-element="tags"]': {
      [`@container (max-width: ${CALENDAR_MOBILE_BREAKPOINT})`]: {
        paddingTop: 'var(--bui-space-2)',
        justifyContent: 'center',
      },

      '& [class*="MuiChip"]': {
        margin: 0,
      },
    },
    '& [class*="BackstageTable-root"]': {
      paddingTop: 'var(--bui-space-4)',
    },
    '& div[data-calendar-element="tags"]': {
      paddingBottom: widgetMode ? 'var(--bui-space-2)' : '0',
    },
  }),
}));

interface CalendarContainerProps {
  widgetMode?: boolean;
  children: React.ReactNode;
}
export const CalendarContainer = ({ widgetMode = false, children }: CalendarContainerProps) => {
  const classes = useStyles({ widgetMode: widgetMode });
  return (
    <div className={classes.containerWrapper}>
      <div data-calendar-container="true" className={classes.tableContainer}>
        {children}
      </div>
    </div>
  );
};
